import { CallEventsService } from '@gem/call/CallController/Services/CallEventsService/CallEventsService';
import { CallWaitingRoomService } from '@gem/call/CallController/Services/CallWaitingRoomService/CallWaitingRoomService';
import { ProviderRemoteStreamType } from '@gem/call/CallProviders/callProviders.constants';
import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { Call } from '@gem/call/CallStore/Call';
import { Opponent } from '@gem/call/CallStore/Opponent/Opponent';
import * as callModel from '@gem/call/Model/callModel';
import {
  CallMemberStatus,
  ICallInfo,
  ICallMember,
  WaitingRoomRequestStatus,
} from '@gem/call/Model/callModel.types';
import { StatusCallFinishReason } from '@gem/call/Model/callModel.types';
import {
  ICallParticipantIdToRole,
  ICallPermissions,
  ICallPersonalizedPermissions,
  PermissionRequestAccess,
  PermissionsRequestStatuses,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { CallEventTypes } from '@gem/call/constants';
import { CallBlurType } from '@gem/call/constants';
import { IDevices } from '@gem/call/tasks/getDefaultDevices';
import { t } from '@gem/modules/i18n';
import { logger } from '@gem/modules/logger';
import { popupsTasks } from '@gem/popups';

import {
  IAddOpponentToCallParams,
  ICallControllerConstructorParams,
  ICallControllerHandlers,
  ICallPublishProps,
  IEventListItem,
  ISetCallInfoProps,
  IStartCallProps,
  IStartRecordingParams,
} from './CallController.types';
import { CallMembersService } from './Services/CallMembersService/CallMembersService';
import { CallPermissionsService } from './Services/CallPermissionsService/CallPermissionsService';
import { CallProviderService } from './Services/CallProviderService/CallProviderService';
import { CallRecordingService } from './Services/CallRecordingService/CallRecordingService';
import { CallStateHandlerService } from './Services/CallStateHandlerService/CallStateHandlerService';
import { CallUsersService } from './Services/CallUsersService/CallUsersService';

export class CallController {
  callingTimer: ReturnType<typeof setInterval> | null;
  durationInterval: ReturnType<typeof setInterval> | null;
  call: Call;
  canUseBlur: boolean;
  devices: IDevices;
  callProviderService: CallProviderService;
  private callStateHandlerService: CallStateHandlerService | null;
  handlers: ICallControllerHandlers = {} as ICallControllerHandlers;
  private callUsersService: CallUsersService;
  private callPermissionsService: CallPermissionsService;
  private callRecordingService: CallRecordingService;
  private callMemberService: CallMembersService;
  private callEventsService: CallEventsService;
  private callWaitingRoomService: CallWaitingRoomService;

  constructor(params: ICallControllerConstructorParams) {
    this.call = params.call;
    this.handlers = params.handlers;
    this.callEventsService = new CallEventsService({ call: params.call });
    this.callWaitingRoomService = new CallWaitingRoomService({ call: params.call });
    this.callUsersService = new CallUsersService({
      handlers: {
        changeVideoStreamQuality: this.changeVideoStreamQuality.bind(this),
        addEvent: this.addEvent.bind(this),
        getCallMembersByUid: this.getCallMembersByUid.bind(this),
      },
      call: params.call,
    });
    this.callRecordingService = new CallRecordingService({ call: params.call });
    this.callMemberService = new CallMembersService({ call: params.call });
    this.callPermissionsService = new CallPermissionsService({
      call: params.call,
      addEvent: this.addEvent.bind(this),
    });
    this.callProviderService = new CallProviderService({
      providerUrl: params.providerUrl,
      call: params.call,
      providerType: params.call.providerType,
      withVirtualBackground: !params.call.isUnauthorizedCall,
    });

    this.canUseBlur = this.callProviderService.canUseBlur;
    if (this.call.channelId && !this.call.isUnauthorizedCall) {
      this.initCallStateService(this.call.channelId);
    }
  }

  async dispose(
    isInternalFinish = true,
    justILeaveCall?: boolean,
    statusCallFinishReason?: StatusCallFinishReason
  ): Promise<void> {
    if (this.call.isDisposing) {
      return;
    }
    this.call.setIsDisposing(true);
    this.callProviderService.dispose();
    this.clearCallingTimer.call(this);

    if (this.durationInterval) {
      clearInterval(this.durationInterval);
    }
    if (this.callStateHandlerService) {
      this.callStateHandlerService.dispose();
    }
    await this.call.dispose();

    if (this.handlers.onDeleteCall) {
      this.handlers.onDeleteCall({
        isInternalFinish,
        justILeaveCall,
        statusCallFinishReason,
        timeLimit: this.call.callLimits.maxDuration || undefined,
      });
    }
    this.call.setIsDisposing(false);
  }

  /**
   * Call service segment. All call service methods.
   */

  initCallStateService(channelId: string): void {
    if (this.callStateHandlerService) {
      return;
    }
    this.callStateHandlerService = new CallStateHandlerService({
      call: this.call,
      channelId: channelId,
      handlers: {
        feedbackHandler: this.handlers.feedbackHandler,
        addEvent: this.addEvent.bind(this),
        startBusy: this.startBusy.bind(this),
        dispose: this.dispose.bind(this),
        clearCallingTimer: this.clearCallingTimer.bind(this),
        getCallingTimer: this.getCallingTimer.bind(this),
        muteAudio: this.muteAudio.bind(this),
        muteBroadcasting: this.muteBroadcasting.bind(this),
        addOpponentToCall: this.addOpponentToCall.bind(this),
        getCallMembers: this.getCallMembers.bind(this),
        getCallMembersByIds: this.getCallMembersByIds.bind(this),
        checkUserLeftAndDelete: this.callUsersService.checkUserLeftAndDelete.bind(
          this.callUsersService
        ),
        updateViewState: this.updateViewState.bind(this),
      },
    });

    this.callStateHandlerService.connect();
  }

  async startCall(props: IStartCallProps): Promise<void> {
    const canUserConnect = await this.checkCanUserConnectToCall(props.callInfo);

    if (!canUserConnect) {
      this.dispose();
      return;
    }

    if (this.call.opponents.meOpponent) {
      this.call.opponents.meOpponent.setUid(props.callInfo.audioVideoUid);
    }

    await this.getCallPermissions(props.callInfo.channelId);
    await this.getCallMembers(props.callInfo.channelId);
    this.setCallInfo({
      callInfo: props.callInfo,
      withoutTimer: props.withoutTimer,
    });
    await this.initCall();

    await this.joinAndPublish({
      isVideoMuted: props.isVideoMuted,
      isAudioMuted: props.isAudioMuted,
    });

    if (
      this.call.uiFeatures.permissionsAndAccessControlEnabled &&
      this.call.isMeInitiatorOwnerOrAdmin
    ) {
      await this.getPermissionRequests(props.callInfo.channelId);
    }
    this.startTimer();
    this.call.setStarted(true);
  }

  startTimer(): void {
    this.durationInterval = setInterval(() => {
      const currentDate = new Date().getTime();
      const startDate =
        this.call.startTs !== null
          ? new Date(this.call.startTs - this.call.approxDurationMs).getTime()
          : currentDate;
      const duration = currentDate - startDate;
      this.call.setDuration(duration);
    }, 1000);
  }

  async initCall(): Promise<void> {
    await this.callProviderService.initCall({
      handlers: {
        onUserJoined: this.callUsersService.onUserJoined.bind(this.callUsersService),
        onUserLeft: this.callUsersService.onUserLeft.bind(this.callUsersService),
        onLeave: this.callUsersService.onLeave.bind(this.callUsersService),
        onUserMediaChanged: this.callUsersService.onUserMediaChangedHandler.bind(
          this.callUsersService
        ),
        onUserHandAction: this.onUserHandAction.bind(this),
        onUserVolumeLevelChanged: this.callUsersService.onUserVolumeLevelChangedHandler.bind(
          this.callUsersService
        ),
      },
    });
  }

  setCallInfo(props: ISetCallInfoProps): void {
    const { callInfo, withoutTimer } = props;
    this.call.setChannelId(callInfo.channelId);
    this.call.approxDurationMs = props.callInfo.approxDurationMs;
    this.call.setInitiatorId(callInfo.initiatorId);
    this.call.setIsCallOwnerOrAdmin(props.callInfo.isAdmin);
    if (callInfo.permissions) {
      this.call.permissions.setPermissions(callInfo.permissions);
    }
    this.call.setMaxBroadcasters(callInfo.maxBroadcasters);
    this.call.members.setMembers(callInfo.members);
    this.call.setProviderUid(callInfo.audioVideoUid);
    this.call.uiFeatures.setUiFeatures(callInfo.uiFeatures);

    this.call.setCallLimits({
      maxParticipants: callInfo.maxParticipants,
      maxParticipantsReminder: callInfo.maxParticipantsReminder || null,
      maxDuration: callInfo.maxDuration || null,
      maxDurationReminder: callInfo.maxDurationReminder || null,
      videoQualitySettings: callInfo.videoQualitySettings,
    });

    if (callInfo.callVisibleDuration && !withoutTimer) {
      this.setCallingTimer(callInfo.callVisibleDuration);
    } else {
      this.call.setStartTs(Number(new Date()));
    }

    if ('recordingId' in callInfo && callInfo.recordingId) {
      this.call.startRecording(callInfo.recordingId!);
    }

    if (callInfo.callName) {
      this.call.setAvatar({
        title: callInfo.callName,
        url: null,
        colorIndex: null,
      });
    }
    this.call.meStore.setDeviceData({
      deviceId: callInfo.deviceId,
      audioVideoToken: callInfo.audioVideoToken,
      audioVideoUid: callInfo.audioVideoUid,
      shareScreenToken: callInfo.shareScreenToken,
      shareScreenUid: callInfo.shareScreenUid,
    });

    const me = callInfo.members.find((item) => item.userId === this.call.meStore.id);

    if (me && me.isHandRaised) {
      this.call.meStore.setHandRaised(me.isHandRaised, me.handRaisedAt);
    }
  }

  async joinAndPublish(params: ICallPublishProps): Promise<void> {
    const { isVideoMuted, isAudioMuted } = params;

    const success = await this.joinCall();

    if (!success) {
      return;
    }

    if (!this.call.canUnmute) {
      await this.unmuteAudioVideo({
        isVideoMuted: true,
        isAudioMuted: true,
      });
    } else {
      await this.unmuteAudioVideo({
        isVideoMuted: isVideoMuted,
        isAudioMuted: isAudioMuted,
      });
    }
  }

  async joinCall(): Promise<boolean> {
    if (
      !this.call.channelId ||
      !this.call.meStore.audioVideoToken ||
      !this.call.meStore.audioVideoUid ||
      !this.call.meStore.shareScreenToken ||
      !this.call.meStore.shareScreenUid
    ) {
      return false;
    }

    if (this.call.isUnauthorizedCall) {
      this.initCallStateService(this.call.channelId);
    }
    await this.callProviderService.startCall();

    return true;
  }

  async unmuteAudioVideo({ isVideoMuted = true, isAudioMuted = true }): Promise<void> {
    if (!isAudioMuted || !this.call.meStore.isAudioMuted) {
      await this.unmuteAudio(true);
    }

    if (!isVideoMuted || !this.call.meStore.isVideoMuted) {
      await this.unmuteVideo(true);
    }

    if (this.handlers.onFirstVideoAudioStarted) {
      this.handlers.onFirstVideoAudioStarted();
    }
  }

  async muteAudio(): Promise<void> {
    await this.callProviderService.muteAudio();
  }

  async unmuteAudio(nedToPublish: boolean): Promise<void> {
    await this.callProviderService.unmuteAudio(nedToPublish);
  }

  async muteVideo(): Promise<void> {
    await this.callProviderService.muteVideo();
  }

  async unmuteVideo(needToPublish: boolean): Promise<void> {
    await this.callProviderService.unmuteVideo(needToPublish);
  }

  async checkCameraPermission(isInitial?: boolean): Promise<boolean> {
    return this.callProviderService.checkCameraPermission(isInitial);
  }

  async checkAudioPermission(): Promise<boolean> {
    return this.callProviderService.checkAudioPermission();
  }

  playOpponentsAudio(): void {
    this.callProviderService.playOpponentsAudio();
    if (this.call.viewState.isWaitingAudioShow) {
      this.call.viewState.setIsWaitingAudioShow(false);
    }
  }

  async stopScreenSharing(): Promise<void> {
    if (this.call.meStore.isSharedScreen && this.call.meStore.shareScreenUid) {
      this.call.opponents.deleteOpponent(this.call.meStore.shareScreenUid);
      await this.callProviderService.stopScreenShare();
    }
  }

  async startScreenSharing(electronSourceId?: string): Promise<void> {
    const isScreenSharing =
      this.call.isScreenSharing && this.call.opponents.screenSharingOpponent?.hasVideo;

    if (isScreenSharing) {
      popupsTasks.showToast(t('screen_sharing_error'));
      return;
    }

    try {
      await this.callProviderService.startScreenShare({
        electronSourceId,
        onStopSharingScreen: this.stopScreenSharing.bind(this),
      });
    } catch (e) {
      logger.get('Tasks').error('calls.screenSharing', e);
    }
  }

  playOpponentVideo(videoContainer: HTMLDivElement, uid: ProviderUid): void {
    this.callProviderService.playOpponentVideo(videoContainer, uid);

    if (this.call.meStore.audioVideoUid === uid) {
      this.playPublisherVideo(videoContainer);
      return;
    }

    const opponent = this.call.opponents.getOpponent(uid);

    opponent?.setIsPlayingVideo(true);
  }

  stopOpponentVideo(uid: ProviderUid): void {
    this.callProviderService.stopOpponentVideo(uid);

    if (this.call.meStore.audioVideoUid === uid) {
      this.stopPublisherVideo();
      return;
    }

    const opponent = this.call.opponents.getOpponent(uid);
    opponent?.setIsPlayingVideo(false);
  }

  playPublisherVideo(videoContainer: HTMLDivElement): HTMLVideoElement | null {
    const videoNode = this.callProviderService.playPublisherVideo(videoContainer);

    this.call.meStore.setIsPlayingVideo(true);

    return videoNode;
  }

  stopPublisherVideo(container?: HTMLVideoElement): void {
    this.callProviderService.stopPublisherVideo(container);

    this.call.meStore.setIsPlayingVideo(false);
  }

  setBlur(blurType: CallBlurType): void {
    this.callProviderService.setBlur(blurType);
  }

  async changeVideoStreamQuality(uid: ProviderUid, isLowQuality: boolean): Promise<void> {
    const streamType = isLowQuality
      ? ProviderRemoteStreamType.LowStream
      : ProviderRemoteStreamType.HighStream;
    await this.callProviderService.setSomeUserTypeStream(uid, streamType);
  }

  addOpponentToCall(data: IAddOpponentToCallParams): void {
    this.callUsersService.addOpponentToCall(data);
  }

  updateResolution(): void {
    this.callUsersService.updateResolution();
  }

  async setCurrentDeviceId(kind: MediaDeviceKind, deviceId: string): Promise<void> {
    await this.callProviderService.setCurrentDeviceId(kind, deviceId);
  }

  muteBroadcasting(muteAudio: boolean, muteVideo: boolean, stopScreenSharing: boolean): void {
    if (muteAudio) {
      this.muteAudio();
    }
    if (muteVideo) {
      this.muteVideo();
    }
    if (stopScreenSharing) {
      this.stopScreenSharing();
    }
    if (this.call.channelId) {
      this.getCallPermissions(this.call.channelId);
    }
  }

  updateViewState(hideMembers: boolean): void {
    if (hideMembers) {
      this.call.viewState.hideMembers();
    }
  }

  /**
   * Toggle hand segment
   */

  onUserHandAction(uid: ProviderUid, value: boolean, handRaisedAt: number | null): void {
    const memberDevice = this.call.members.getMemberDeviceByUid(uid);

    if (memberDevice) {
      value &&
        this.callEventsService.addEvent({
          userId: memberDevice.memberId,
          type: CallEventTypes.RaiseHand,
        });
      memberDevice.setHandRaised(value, handRaisedAt);
    }
  }

  async toggleHand(): Promise<void> {
    const isHandRaised = this.call.meStore.isHandRaised;
    const handRaisedAt = isHandRaised ? null : Date.now();

    const success = await this.callProviderService.setHandRaised(!isHandRaised, handRaisedAt);
    if (success) {
      this.call.meStore.setHandRaised(!isHandRaised, handRaisedAt);
    }
  }

  /**
   * Permissions service segment. All call permissions methods.
   */

  async getCallPermissions(channelId: string): Promise<void> {
    await this.callPermissionsService.getCallPermissions(channelId);
  }

  async setCallPermissionsForAll(
    channelId: string,
    permissions: Required<ICallPermissions>
  ): Promise<void> {
    await this.callPermissionsService.setCallPermissionsForAll(channelId, permissions);
  }

  async setCallPermissionsForUser(
    channelId: string,
    userId: string,
    permissions: ICallPersonalizedPermissions
  ): Promise<void> {
    await this.callPermissionsService.setCallPermissionsForUser(channelId, userId, permissions);
  }

  async clearCallPermissionsForUser(channelId: string, userId: string): Promise<void> {
    await this.callPermissionsService.clearCallPermissionsForUser(channelId, userId);
  }

  async updatePermissionRequestsForAll(params: {
    channelId: string;
    status: PermissionsRequestStatuses;
  }): Promise<void> {
    await this.callPermissionsService.updatePermissionRequestsForAll(params);
  }

  async updatePermissionRequestForUser(params: {
    channelId: string;
    userId: string;
    access: PermissionRequestAccess;
    status: PermissionsRequestStatuses;
  }): Promise<void> {
    await this.callPermissionsService.updatePermissionRequestForUser(params);
  }

  async createPermissionRequest(params: {
    channelId: string;
    access: PermissionRequestAccess;
  }): Promise<any> {
    return await this.callPermissionsService.createPermissionRequest(params);
  }

  async getPermissionRequests(channelId: string): Promise<void> {
    await this.callPermissionsService.getPermissionRequests(channelId);
  }

  async setCallParticipantsRoles(
    channelId: string,
    participantIdsToRole: ICallParticipantIdToRole[]
  ): Promise<void> {
    await this.callPermissionsService.setCallParticipantsRoles(channelId, participantIdsToRole);
  }

  /**
   * Recording service segment. All call recording methods.
   */

  async startRecording(params: IStartRecordingParams): Promise<void> {
    await this.callRecordingService.startRecording(params);
  }

  async stopRecording(): Promise<void> {
    await this.callRecordingService.stopRecording();
  }

  /**
   * Member service segment. All call member methods.
   */

  async checkCanUserConnectToCall(callInfo: ICallInfo): Promise<boolean> {
    const joinedMembers = callInfo.members.filter(
      ({ status }: ICallMember) => (status as CallMemberStatus) === CallMemberStatus.OnCall
    );
    const myId = this.call.meStore.id;
    if (callInfo.initiatorId.toString() !== myId) {
      const isJoinedInitiator = joinedMembers.some(
        ({ userId }: ICallMember) => userId.toString() === callInfo.initiatorId.toString()
      );
      const maxParticipants = this.call.callLimits.maxParticipants - (isJoinedInitiator ? 0 : 1);
      if (joinedMembers.length >= maxParticipants) {
        popupsTasks.showAlert(t('calls_group_call_limit_exceed_alert'));

        return false;
      }
    }

    return true;
  }

  async getCallMembers(channelId: string): Promise<void> {
    const response = await callModel.getCallMembers({ channelId });
    this.call.members.setMembers(response);
  }

  async getCallMembersByUid(channelId: string, ids: ProviderUid[]): Promise<void> {
    const response = await callModel.getCallMembersByUid({ channelId, ids });
    this.call.members.setMembers(response);
  }

  async getCallMembersByIds(channelId: string, ids: string[]): Promise<void> {
    const response = await callModel.getCallMembersByIds({ channelId, ids });
    this.call.members.setMembers(response);
  }

  removeMemberFromCall(userId: string, deviceId: ProviderUid): void {
    if (!this.call.channelId) {
      return;
    }

    this.callMemberService.removeMemberFromCall(userId, deviceId, this.call.channelId);
  }

  muteMember(userId: string, deviceId: ProviderUid): void {
    if (!this.call.channelId) {
      return;
    }

    this.callMemberService.muteMember(userId, deviceId, this.call.channelId);
  }

  /**
   * View members service segment.
   */

  toggleGridLayout(opponent: Opponent | null): void {
    this.call.viewState.toggleGridLayout(opponent);
    this.updateResolution();
  }

  setActiveOpponent(opponent: Opponent | null): void {
    this.call.viewState.setActiveOpponent(opponent);
    this.updateResolution();
  }

  startBusy(): void {
    this.call.setIsBusy(true);
    setTimeout(this.finishBusy.bind(this), 3000);
  }

  finishBusy(): void {
    this.call.setIsBusy(false);
    this.dispose();
  }

  startNoAnswer(): void {
    this.call.setIsNoAnswer(true);
    setTimeout(this.finishNoAnswer.bind(this), 3000);
  }

  finishNoAnswer(): void {
    this.call.setIsNoAnswer(false);
    this.dispose();
  }

  setCallingTimer = (duration: number): void => {
    this.callingTimer = setTimeout(this.startNoAnswer.bind(this), duration);
  };

  clearCallingTimer(): void {
    if (!this.callingTimer) {
      return;
    }

    clearTimeout(this.callingTimer);
  }

  getCallingTimer(): ReturnType<typeof setInterval> | null {
    return this.callingTimer;
  }

  addEvent(event: IEventListItem): void {
    this.callEventsService.addEvent(event);
  }

  deleteEvent(key: number): void {
    this.callEventsService.deleteEvent(key);
  }

  setCanShowCompactView(value: boolean): void {
    this.call.viewState.setCanShowCompactView(value);
  }

  /**
   * Waiting Rooms service segment
   */

  async updateWaitingRoomForAll(status: WaitingRoomRequestStatus): Promise<void> {
    await this.callWaitingRoomService.updateWaitingRoomForAll(status);
  }

  async updateWaitingRoomForMember(
    memberId: string,
    status: WaitingRoomRequestStatus
  ): Promise<void> {
    await this.callWaitingRoomService.updateWaitingRoomForMember(memberId, status);
  }

  async unpublish(): Promise<void> {
    await this.callProviderService.unpublish();
    if (!this.call.meStore.isVideoMuted) {
      this.call.meStore.setIsVideoMuted(true);
    }
    if (!this.call.meStore.isAudioMuted) {
      this.call.meStore.setIsAudioMuted(true);
    }
  }
}
